import {LocalStorage} from '../../utils/LocalStorage';

/**
 * El borrador del wizard «Nueva línea de control» (card CvdQbnl2).
 *
 * Guarda lo que el usuario decidió en los pasos 1 a 3 y lo que la creación ya dejó hecho. Existe
 * por dos motivos:
 *
 * - **«Vincular más cuentas» sale de la aplicación**: el OAuth de Google, TikTok y LinkedIn navega a
 *   otro dominio y vuelve por una pantalla de callback. Sin el borrador, el usuario volvía al paso 3
 *   con el nombre, el presupuesto y los objetivos en blanco.
 * - **La creación es una cadena de pedidos** (cliente → línea → días → objetivos → cuentas). Si uno
 *   falla, el reintento tiene que retomar desde ahí y no duplicar lo que ya se creó: lo creado se
 *   anota acá apenas el backend lo confirma.
 *
 * Vive en `sessionStorage` y no en `localStorage`: es una sola sesión de trabajo en una pestaña, y
 * no tiene sentido retomar mañana un armado a medias en otra. La clave lleva agencia y usuario, así
 * que un cierre de sesión seguido de otro login en la misma pestaña no hereda nada.
 */

export const STEPS = ['cliente', 'linea', 'cuentas', 'campanas'];
export const DRAFT_VERSION = 1;

const KEY_PREFIX = 'line_wizard_';

export const CLIENT_MODE_EXISTING = 'EXISTING';
export const CLIENT_MODE_NEW = 'NEW';

export const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export function newDraft({clientId = null, clientName = null} = {}) {
    const hasClient = clientId !== null && clientId !== undefined && clientId !== '';
    return {
        version: DRAFT_VERSION,
        client: {
            mode: CLIENT_MODE_EXISTING,
            id: hasClient ? String(clientId) : null,
            name: clientName || '',
            newName: '',
            // Se entró desde un cliente (`?client=`): el paso «Cliente» no se muestra (ver `visibleSteps`).
            fixed: hasClient,
        },
        line: {
            name: '',
            currency: null,
            budget: null,
            period: null,
            partialGoals: [],
            partialGoalsInputMode: null,
            partialGoalsInputDefinition: null,
            // Apagado = corre todos los días, que es lo que hace hoy una línea recién creada.
            schedule: {enabled: false, days: WEEKDAYS.reduce((days, day) => ({...days, [day]: true}), {})},
            // Objetivo 1 y 2 con la forma de `GoalConfiguration`: `null` (apagado) o
            // `{metricName, value, metricConversionConfiguration}`.
            goals: [null, null],
        },
        // Las cuentas elegidas en el paso 3, con lo necesario para vincularlas sin volver a pedir
        // el catálogo: canal, id de la plataforma, nombre y si ya eran del cliente.
        accounts: [],
        // Lo que la creación ya dejó hecho. Cada campo se escribe apenas el backend lo confirma.
        created: {
            clientId: null,
            businessUnitId: null,
            scheduleSaved: false,
            goalsSaved: [false, false],
            configurationsCreated: false,
        },
    };
}

/**
 * Los pasos que ve el usuario.
 *
 * Entrando desde un cliente, el paso «Cliente» no existe: llegaba con ese cliente ya elegido y lo
 * único que hacía era pedir un «Siguiente» (recorrido de fede, 01/10, punto 5). Tampoco se dibuja
 * tildado en el stepper: tildarlo decía que el usuario había hecho algo que nunca vio.
 *
 * Sólo con el cliente de verdad: un borrador «fijo» sin `id` (un sessionStorage corrupto) mandaba de
 * «La línea» a «Cliente» (`reachableStep`) y de vuelta, sin fin (review C-rec-fe-linea-10).
 */
export function visibleSteps(draft) {
    const fixed = !!(draft && draft.client && draft.client.fixed && draft.client.id);
    return fixed ? STEPS.filter(step => step !== 'cliente') : STEPS;
}

/**
 * ¿Los días de corrida dicen algo distinto de «todos los días»? Es lo único que vale la pena aclarar
 * debajo del interruptor: el valor por defecto con un texto fijo no le decía nada a nadie (punto 8).
 * Prendido con los siete días elegidos sigue siendo «todos los días»; con ninguno, lo dice el error.
 */
export function scheduleIsCustom(schedule) {
    if (!schedule || !schedule.enabled) return false;
    const on = WEEKDAYS.filter(day => schedule.days && schedule.days[day]).length;
    return on > 0 && on < WEEKDAYS.length;
}

/** La entrada al wizard: arranca un borrador nuevo, con el cliente ya elegido si se conoce. */
export function lineWizardEntryPath(clientId) {
    return clientId ? `/lines/new?client=${encodeURIComponent(clientId)}` : '/lines/new';
}

export function accountKey(account) {
    return `${account.channel}:${account.account_id}`;
}

/** El cliente sobre el que se arma la línea: el que se eligió o el que la creación ya dio de alta. */
export function effectiveClientId(draft) {
    if (draft.created.clientId) return String(draft.created.clientId);
    if (draft.client.mode === CLIENT_MODE_EXISTING && draft.client.id) return String(draft.client.id);
    return null;
}

/** Una vez creada la línea, los pasos 1 a 3 ya no se editan: lo que se tocara ahí no se guardaría. */
export function isMaterialized(draft) {
    return !!(draft.created.businessUnitId && draft.created.configurationsCreated);
}

/**
 * ¿El paso ya tiene lo mínimo para dejar avanzar al siguiente? Es la misma vara que aplica cada
 * paso al tocar «Siguiente», reducida a lo que un paso POSTERIOR necesita para funcionar.
 */
function stepIsComplete(draft, step) {
    if (step === 'cliente') {
        if (draft.created.clientId) return true;
        return draft.client.mode === CLIENT_MODE_EXISTING
            ? !!draft.client.id
            : draft.client.newName.trim().length > 0;
    }
    if (step === 'linea') {
        if (draft.created.businessUnitId) return true;
        return draft.line.name.trim().length > 0 && Number(draft.line.budget) > 0;
    }
    if (step === 'cuentas') return isMaterialized(draft);
    return true;
}

/**
 * El paso al que se puede entrar con este borrador: el pedido, o el primero que quedó sin completar.
 *
 * Existe por las entradas que no pasan por «Siguiente» —recargar la página, un link guardado, volver
 * del OAuth en otra pestaña—: sin esto, `/lines/new/cuentas` con el borrador de un cliente sin elegir
 * dibujaba la lista de cuentas de nadie, y `/lines/new/campanas` sin línea creada no tenía nada que
 * mostrar (QA del 25/09).
 */
export function reachableStep(draft, step) {
    const index = STEPS.indexOf(step);
    for (let i = 0; i < index; i++) {
        if (!stepIsComplete(draft, STEPS[i])) return STEPS[i];
    }
    return step;
}

function storageKey() {
    const agencyId = LocalStorage.fetchAgencyId();
    const userId = LocalStorage.fetchUserId();
    if (!agencyId || !userId) return null;
    return `${KEY_PREFIX}${agencyId}_${userId}`;
}

export function loadDraft() {
    const key = storageKey();
    if (!key) return null;
    try {
        const raw = window.sessionStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Una versión distinta es de un wizard que ya no existe: se descarta en silencio.
        if (!parsed || parsed.version !== DRAFT_VERSION || !parsed.client || !parsed.line || !parsed.created) {
            return null;
        }
        return parsed;
    } catch (error) {
        return null;
    }
}

export function saveDraft(draft) {
    const key = storageKey();
    if (!key) return false;
    try {
        window.sessionStorage.setItem(key, JSON.stringify(draft));
        return true;
    } catch (error) {
        // Sin sessionStorage el wizard sigue andando: sólo pierde el borrador al salir por un OAuth.
        return false;
    }
}

export function clearDraft() {
    const key = storageKey();
    if (!key) return;
    try {
        window.sessionStorage.removeItem(key);
    } catch (error) {
        // Nada que limpiar.
    }
}
